import { useEffect, useMemo, useState } from "react";

import { Lock, Plus, Trash2, Unlock } from "lucide-react";

import { Combobox, type ComboOption } from "@/components/cinch/Combobox";
import { NumberInput } from "@/components/cinch/NumberInput";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";

import {
  useAccounts,
  useClosers,
  useCompanyPicker,
  useContractDetail,
  useContractPicker,
  useContractTypes,
  useContractWrites,
  useDiscounts,
  useItems,
  usePackages,
  type RenewalTerm,
} from "../api";
import { Field, writeError } from "./editorCommon";

// Cinch's fixed contract-type ids (ContractType has no behaviour flags — the
// employee-portal modal keys the conditional fields off these same constants).
const REPLACEMENT_TYPE = "5b38cedb-5c5f-45eb-bd2d-f1b371fc3efb";
const NEW_TYPE = "b6fe4c47-2b0d-4c97-a77c-2256479d023c";
const REFERRAL_TYPE = "3107699e-513d-474f-9d2a-94b14401d05f";
const PURCHASE_ORDER_TYPE = "3107699e-513d-474f-9d2a-94b14401d05f"; // same id as referral (per Cinch)
const UPSELL_TYPE = "76489f7c-2637-4cdf-aecb-b9233f4dc482";
const RESELLER_TYPE = "9f5b4da8-d49f-4f53-8cdd-9f9eca3c67ec";

const WHOLE_CONTRACT = "__whole__";

interface LineRow {
  _id: string; // doubles as the write-only client_ref
  product: string | null; // "item:<id>" | "package:<id>"
  qty: number | null;
  price_per_unit: number | null;
  overage_price_per_unit: number | null;
}
interface DiscRow {
  _id: string;
  discount: string | null;
  target: string; // a line _id, or WHOLE_CONTRACT
  duration_override: number | null;
}

let _seq = 0;
const uid = (p: string) => `${p}${_seq++}`;
const dateOnly = (s: string | null | undefined) => (s ? s.slice(0, 10) : "");

const RENEWAL_TERMS: { value: RenewalTerm; label: string }[] = [
  { value: "SAME_TERM", label: "Same term" },
  { value: "DIFFERENT_TERM", label: "A different term" },
  { value: "MONTH_TO_MONTH", label: "Month-to-month" },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-3">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</span>
      {children}
    </div>
  );
}

/** Create/edit a Cinch billing contract at employee-portal parity. The contract
 *  TYPE drives conditional required fields: Replacement → replaced contract;
 *  Upsell → amended contract (+ hides renewal/term, which it inherits); Purchase
 *  Order → reseller contract + assigned company. Replacement/Upsell also require
 *  a billing-effective-from. Fetches its own detail for edit. */
export function ContractEditor({
  open,
  contractId,
  onClose,
}: {
  open: boolean;
  contractId: string | null;
  onClose: () => void;
}) {
  const { save } = useContractWrites();
  const detailQ = useContractDetail(open && contractId ? contractId : null);
  const detail = detailQ.data;

  const [account, setAccount] = useState<string | null>(null);
  const [contractType, setContractType] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState("");
  const [companyDba, setCompanyDba] = useState("");
  const [replacedContract, setReplacedContract] = useState<string | null>(null);
  const [amendedContract, setAmendedContract] = useState<string | null>(null);
  const [resellerContract, setResellerContract] = useState<string | null>(null);
  const [assignedCompany, setAssignedCompany] = useState<string | null>(null);
  const [signDate, setSignDate] = useState("");
  const [billingFrom, setBillingFrom] = useState("");
  const [paymentType, setPaymentType] = useState("CC");
  const [termMonths, setTermMonths] = useState<number | null>(12);
  const [baseFee, setBaseFee] = useState<number | null>(0);
  const [initialContacts, setInitialContacts] = useState<number | null>(0);
  const [tenKMore, setTenKMore] = useState<number | null>(0);
  const [useEmailCount, setUseEmailCount] = useState(false);
  const [customTerms, setCustomTerms] = useState(false);
  const [mrrTotal, setMrrTotal] = useState<number | null>(0);
  const [mrrManual, setMrrManual] = useState(false);
  const [autoRenew, setAutoRenew] = useState(true);
  const [renewalTerm, setRenewalTerm] = useState<RenewalTerm>("SAME_TERM");
  const [renewalTermMonths, setRenewalTermMonths] = useState<number | null>(null);
  const [mtmUplift, setMtmUplift] = useState<number | null>(null);
  const [noticeDays, setNoticeDays] = useState<number | null>(30);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [title, setTitle] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [ccEmail, setCcEmail] = useState("");
  const [bccEmail, setBccEmail] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [stateVal, setStateVal] = useState("");
  const [zip, setZip] = useState("");
  const [closerId, setCloserId] = useState<string | null>(null);
  const [closerHubspot, setCloserHubspot] = useState("");
  const [pandadocId, setPandadocId] = useState("");
  const [stripeId, setStripeId] = useState("");
  const [hubspotDealId, setHubspotDealId] = useState("");
  const [editThirdParty, setEditThirdParty] = useState(false);
  const [lines, setLines] = useState<LineRow[]>([]);
  const [discs, setDiscs] = useState<DiscRow[]>([]);

  const isReplacement = contractType === REPLACEMENT_TYPE;
  const isUpsell = contractType === UPSELL_TYPE;
  const isPurchaseOrder = contractType === PURCHASE_ORDER_TYPE;

  // ── reference data / pickers ────────────────────────────────────────────────
  const accountsQ = useAccounts({ limit: 1000, offset: 0 });
  const typesQ = useContractTypes({ limit: 500, offset: 0 });
  const itemsQ = useItems({ limit: 500, offset: 0 });
  const packagesQ = usePackages({ limit: 500, offset: 0 });
  const discountsQ = useDiscounts({ limit: 500, offset: 0 });
  const closersQ = useClosers();
  const excl = contractId ?? undefined;
  const replacedQ = useContractPicker(
    { limit: 100, offset: 0, account: account ?? "", contract_type: `${REPLACEMENT_TYPE},${NEW_TYPE}`, ...(excl ? { exclude_id: excl } : {}) },
    open && isReplacement && !!account,
  );
  const amendedQ = useContractPicker(
    { limit: 100, offset: 0, account: account ?? "", contract_type: `${REPLACEMENT_TYPE},${NEW_TYPE},${PURCHASE_ORDER_TYPE},${REFERRAL_TYPE}`, ...(excl ? { exclude_id: excl } : {}) },
    open && isUpsell && !!account,
  );
  const resellerQ = useContractPicker(
    { limit: 100, offset: 0, account: account ?? "", contract_type: RESELLER_TYPE },
    open && isPurchaseOrder && !!account,
  );
  const companyPickerQ = useCompanyPicker(open && isPurchaseOrder);

  const accountOptions: ComboOption[] = (accountsQ.data?.results ?? []).map((a) => ({ id: a.id, label: a.name || a.id }));
  const typeOptions: ComboOption[] = (typesQ.data?.results ?? []).map((t) => ({ id: t.id, label: t.name || t.id }));
  // Any "*upsell*" type (Upsell Items, Purchase Order Upsell, …) appends to a parent
  // contract's terms, so it carries no renewal/term of its own — hide + omit them.
  // (The Amended-Contract field is stricter: only the exact Upsell Items type id.)
  const selectedTypeName = ((typesQ.data?.results ?? []).find((t) => t.id === contractType)?.name ?? "").toLowerCase();
  const isUpsellByName = selectedTypeName.includes("upsell");
  const closerOptions: ComboOption[] = (closersQ.data?.results ?? []).map((c) => ({ id: c.id, label: c.name || c.id }));
  const companyOptions: ComboOption[] = (companyPickerQ.data?.results ?? []).map((c) => ({ id: c.id, label: c.name || c.id }));
  const contractOpts = (rows: { id: string; company_name: string | null; sign_date: string | null }[] | undefined): ComboOption[] =>
    (rows ?? []).map((r) => ({ id: r.id, label: r.company_name || r.id, subtitle: r.sign_date ? `Signed ${dateOnly(r.sign_date)}` : undefined }));
  const itemMap = useMemo(() => new Map((itemsQ.data?.results ?? []).map((i) => [i.id, i])), [itemsQ.data]);
  const pkgMap = useMemo(() => new Map((packagesQ.data?.results ?? []).map((p) => [p.id, p])), [packagesQ.data]);
  const productOptions: ComboOption[] = useMemo(
    () => [
      ...(itemsQ.data?.results ?? []).map((i) => ({ id: `item:${i.id}`, label: i.name || i.id, group: "Items" })),
      ...(packagesQ.data?.results ?? []).map((p) => ({ id: `package:${p.id}`, label: p.name || p.id, group: "Packages" })),
    ],
    [itemsQ.data, packagesQ.data],
  );
  const discountOptions: ComboOption[] = (discountsQ.data?.results ?? []).map((d) => ({
    id: d.id,
    label: d.code ? `${d.name} (${d.code})` : d.name || d.id,
    subtitle: d.type ?? undefined,
  }));

  // ── prefill ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    if (contractId && !detail) return;
    const c = detail;
    setAccount(c?.account ?? null);
    setContractType(c?.contract_type ?? null);
    setCompanyName(c?.company_name ?? "");
    setCompanyDba(c?.company_dba ?? "");
    setReplacedContract(c?.replaced_contract_id ?? null);
    setAmendedContract(c?.amended_contract ?? null);
    setResellerContract(c?.reseller_contract_id ?? null);
    setAssignedCompany(c?.assigned_company_id ?? null);
    setSignDate(dateOnly(c?.sign_date));
    setBillingFrom(dateOnly(c?.billing_effective_from));
    setPaymentType(c?.payment_type ?? "CC");
    setTermMonths(c?.term_months ?? 12);
    setBaseFee(c?.base_fee ?? 0);
    setInitialContacts(c?.initial_contacts ?? 0);
    setTenKMore(c?.ten_k_more ?? 0);
    setUseEmailCount(!!c?.use_email_count);
    setCustomTerms(!!c?.custom_terms);
    setMrrTotal(c?.mrr_total ?? 0);
    setMrrManual(false);
    setAutoRenew(c?.is_auto_renew ?? true);
    setRenewalTerm((c?.renewal_term as RenewalTerm) ?? "SAME_TERM");
    setRenewalTermMonths(c?.renewal_term_months ?? null);
    setMtmUplift(c?.mtm_uplift_pct ?? null);
    setNoticeDays(c?.notice_period_days ?? 30);
    setFirstName(c?.first_name ?? "");
    setLastName(c?.last_name ?? "");
    setTitle(c?.title ?? "");
    setEmail(c?.email ?? "");
    setPhone(c?.phone ?? "");
    setCcEmail(c?.cc_email ?? "");
    setBccEmail(c?.bcc_email ?? "");
    setStreet(c?.street ?? "");
    setCity(c?.city ?? "");
    setStateVal(c?.state ?? "");
    setZip(c?.zip ?? "");
    setCloserId(c?.closer_id ?? null);
    setCloserHubspot(c?.closer_hubspot_owner_id ?? "");
    setPandadocId(c?.pandadoc_id ?? "");
    setStripeId(c?.stripe_customer_id ?? "");
    setHubspotDealId(c?.hubspot_deal_id ?? "");
    setEditThirdParty(false);
    const uuidToRef = new Map<string, string>();
    const nextLines = (c?.contract_line_items ?? []).map((li) => {
      const _id = uid("line");
      if (li.uuid) uuidToRef.set(li.uuid, _id);
      return {
        _id,
        product: li.package ? `package:${li.package}` : li.item ? `item:${li.item}` : null,
        qty: li.qty ?? null,
        price_per_unit: li.price_per_unit ?? null,
        overage_price_per_unit: li.overage_price_per_unit ?? null,
      };
    });
    setLines(nextLines);
    setDiscs(
      (c?.contract_discounts ?? []).map((cd) => ({
        _id: uid("disc"),
        // ContractDiscount.discount id isn't in the read type; fall back to null-safe access.
        discount: (cd as { discount?: string | null }).discount ?? null,
        target: cd.target_line_item ? (uuidToRef.get(cd.target_line_item) ?? WHOLE_CONTRACT) : WHOLE_CONTRACT,
        duration_override: cd.duration_override ?? null,
      })),
    );
    save.reset();
  }, [open, contractId, detail]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── MRR auto-calc (base fee + line totals) unless manually overridden ────────
  const recomputeMrr = (nextLines: LineRow[], nextBase: number | null) => {
    if (mrrManual) return;
    const lineTotal = nextLines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.price_per_unit) || 0), 0);
    setMrrTotal(Number(((Number(nextBase) || 0) + lineTotal).toFixed(6)));
  };

  const patchLine = (id: string, patch: Partial<LineRow>) => {
    setLines((ls) => {
      const next = ls.map((l) => (l._id === id ? { ...l, ...patch } : l));
      recomputeMrr(next, baseFee);
      return next;
    });
  };
  const patchDisc = (id: string, patch: Partial<DiscRow>) =>
    setDiscs((ds) => ds.map((d) => (d._id === id ? { ...d, ...patch } : d)));
  const removeLine = (id: string) =>
    setLines((ls) => {
      const next = ls.filter((x) => x._id !== id);
      recomputeMrr(next, baseFee);
      // re-point any per-line discount that targeted the removed line to whole-contract
      setDiscs((ds) => ds.map((d) => (d.target === id ? { ...d, target: WHOLE_CONTRACT } : d)));
      return next;
    });

  // ── per-line + blended margin (commission risk) ──────────────────────────────
  const lineMarginPct = (l: LineRow): number | null => {
    const price = Number(l.price_per_unit) || 0;
    if (l.product?.startsWith("package:")) {
      const p = pkgMap.get(l.product.slice(8));
      return p?.estimated_gross_margin != null ? p.estimated_gross_margin * 100 : null;
    }
    if (l.product?.startsWith("item:")) {
      const it = itemMap.get(l.product.slice(5));
      if (!it || it.unit_cost == null || !price) return null;
      return ((price - Number(it.unit_cost)) / price) * 100;
    }
    return null;
  };
  const belowFloor = (l: LineRow): boolean => {
    if (!l.product?.startsWith("item:")) return false;
    const it = itemMap.get(l.product.slice(5));
    const price = Number(l.price_per_unit) || 0;
    return !!(it && it.floor_price != null && price && price < Number(it.floor_price));
  };
  const marginTone = (m: number | null) =>
    m == null ? "text-muted-foreground" : m >= 65 ? "text-success" : m >= 50 ? "text-warning" : "text-danger";

  // ── submit gating ────────────────────────────────────────────────────────────
  const missingReqs =
    !account ||
    !contractType ||
    !companyName.trim() ||
    (isReplacement && !replacedContract) ||
    (isUpsell && !amendedContract) ||
    (isPurchaseOrder && (!resellerContract || !assignedCompany)) ||
    ((isReplacement || isUpsell) && !billingFrom);

  const submit = () => {
    const body: Record<string, unknown> = {
      account,
      contract_type: contractType,
      company_name: companyName.trim() || null,
      company_dba: companyDba.trim() || null,
      replaced_contract_id: isReplacement ? replacedContract : null,
      amended_contract: isUpsell ? amendedContract : null,
      reseller_contract_id: isPurchaseOrder ? resellerContract : null,
      assigned_company_id: isPurchaseOrder ? assignedCompany : null,
      sign_date: signDate || null,
      billing_effective_from: billingFrom || null,
      payment_type: paymentType,
      term_months: termMonths,
      base_fee: baseFee,
      initial_contacts: initialContacts,
      ten_k_more: tenKMore,
      use_email_count: useEmailCount,
      custom_terms: customTerms,
      mrr_total: mrrTotal,
      first_name: firstName.trim() || null,
      last_name: lastName.trim() || null,
      title: title.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      cc_email: ccEmail.trim() || null,
      bcc_email: bccEmail.trim() || null,
      street: street.trim() || null,
      city: city.trim() || null,
      state: stateVal.trim() || null,
      zip: zip.trim() || null,
      closer_id: closerId,
      closer_hubspot_owner_id: closerHubspot.trim() || null,
      pandadoc_id: pandadocId.trim() || null,
      stripe_customer_id: stripeId.trim() || null,
      hubspot_deal_id: hubspotDealId.trim() || null,
      // Upsell-family types inherit the parent's renewal/term; don't send renewal settings.
      ...(isUpsellByName
        ? {}
        : {
            is_auto_renew: autoRenew,
            renewal_term: renewalTerm,
            renewal_term_months: renewalTerm === "DIFFERENT_TERM" ? renewalTermMonths : null,
            mtm_uplift_pct: renewalTerm === "MONTH_TO_MONTH" ? mtmUplift : null,
            notice_period_days: noticeDays,
          }),
      contract_line_items: lines
        .filter((l) => l.product)
        .map((l) => {
          const [kind, id] = (l.product as string).split(":");
          const qty = l.qty ?? 1;
          const price = l.price_per_unit ?? 0;
          return {
            client_ref: l._id,
            item: kind === "item" ? id : null,
            package: kind === "package" ? id : null,
            qty,
            price_per_unit: price,
            item_total: Number((qty * price).toFixed(6)),
            overage_price_per_unit: l.overage_price_per_unit ?? 0,
          };
        }),
      contract_discounts: discs
        .filter((d) => d.discount)
        .map((d) => {
          const out: Record<string, unknown> = { discount: d.discount };
          if (d.target && d.target !== WHOLE_CONTRACT) out.target_line_item_client_ref = d.target;
          if (d.duration_override != null) out.duration_override = d.duration_override;
          return out;
        }),
    };
    save.mutate({ id: contractId, body }, { onSuccess: onClose });
  };

  const loadingDetail = !!contractId && detailQ.isLoading;
  const lineRevenue = lines.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.price_per_unit) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{contractId ? "Edit contract" : "New contract"}</DialogTitle>
        </DialogHeader>

        {loadingDetail ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading contract…</p>
        ) : (
          <div className="flex flex-col gap-6">
            <Section title="Billing Information">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Company Name *">
                  <Input value={companyName} onChange={(e) => setCompanyName(e.target.value)} />
                </Field>
                <Field label="DBA">
                  <Input value={companyDba} onChange={(e) => setCompanyDba(e.target.value)} />
                </Field>
                <Field label="Account *">
                  <Combobox options={accountOptions} value={account} onChange={setAccount} placeholder="Select an account…" />
                </Field>
                <Field label="Contract Type *">
                  <Combobox options={typeOptions} value={contractType} onChange={setContractType} placeholder="Select a contract type…" />
                </Field>

                {isPurchaseOrder && (
                  <>
                    <Field label="Reseller Contract *">
                      <Combobox
                        options={contractOpts(resellerQ.data?.results)}
                        value={resellerContract}
                        onChange={setResellerContract}
                        placeholder={account ? "Select a reseller contract…" : "Pick an account first"}
                        disabled={!account}
                      />
                    </Field>
                    <Field label="Assigned Company *">
                      <Combobox options={companyOptions} value={assignedCompany} onChange={setAssignedCompany} placeholder="Select a company…" />
                    </Field>
                  </>
                )}
                {isReplacement && (
                  <Field label="Replaced Contract *" className="col-span-2">
                    <Combobox
                      options={contractOpts(replacedQ.data?.results)}
                      value={replacedContract}
                      onChange={setReplacedContract}
                      placeholder={account ? "Select the contract being replaced…" : "Pick an account first"}
                      disabled={!account}
                    />
                  </Field>
                )}
                {isUpsell && (
                  <Field label="Amended Contract *" className="col-span-2">
                    <Combobox
                      options={contractOpts(amendedQ.data?.results)}
                      value={amendedContract}
                      onChange={setAmendedContract}
                      placeholder={account ? "Select the contract being amended…" : "Pick an account first"}
                      disabled={!account}
                    />
                  </Field>
                )}

                <Field label="First Name">
                  <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </Field>
                <Field label="Last Name">
                  <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </Field>
                <Field label="Email">
                  <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                </Field>
                <Field label="Phone">
                  <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                </Field>
                <Field label="BCC Email">
                  <Input value={bccEmail} onChange={(e) => setBccEmail(e.target.value)} />
                </Field>
                <Field label="CC Email">
                  <Input value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} />
                </Field>
                <Field label="Street Address" className="col-span-2">
                  <Input value={street} onChange={(e) => setStreet(e.target.value)} />
                </Field>
                <Field label="City">
                  <Input value={city} onChange={(e) => setCity(e.target.value)} />
                </Field>
                <Field label="State">
                  <Input value={stateVal} onChange={(e) => setStateVal(e.target.value)} />
                </Field>
                <Field label="Zip">
                  <Input value={zip} onChange={(e) => setZip(e.target.value)} />
                </Field>
              </div>
            </Section>

            <Section title="Contract Terms">
              <div className="grid grid-cols-3 gap-4">
                <Field label="MRR Total">
                  <NumberInput
                    value={mrrTotal}
                    onChange={(v) => {
                      setMrrTotal(v);
                      setMrrManual(true);
                    }}
                    prefix="$"
                  />
                </Field>
                <Field label="Base Fee">
                  <NumberInput
                    value={baseFee}
                    onChange={(v) => {
                      setBaseFee(v);
                      recomputeMrr(lines, v);
                    }}
                    prefix="$"
                  />
                </Field>
                <Field label="Term (months)">
                  <NumberInput value={termMonths} onChange={setTermMonths} maxDecimals={0} />
                </Field>
                <Field label="Initial Contacts">
                  <NumberInput value={initialContacts} onChange={setInitialContacts} maxDecimals={0} />
                </Field>
                <Field label="Fee per 10K More">
                  <NumberInput value={tenKMore} onChange={setTenKMore} prefix="$" />
                </Field>
                <Field label="Payment Type">
                  <Select value={paymentType} onValueChange={setPaymentType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CC">Credit Card</SelectItem>
                      <SelectItem value="ACH">ACH</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Sign Date">
                  <Input type="date" value={signDate} onChange={(e) => setSignDate(e.target.value)} />
                </Field>
                <Field label={`Billing Effective From${isReplacement || isUpsell ? " *" : ""}`}>
                  <Input type="date" value={billingFrom} onChange={(e) => setBillingFrom(e.target.value)} />
                </Field>
                <Field label="Use Email Count">
                  <div className="flex h-9 items-center gap-3">
                    <Switch checked={useEmailCount} onCheckedChange={setUseEmailCount} />
                    <span className="text-xs text-muted-foreground">Custom terms</span>
                    <Switch checked={customTerms} onCheckedChange={setCustomTerms} />
                  </div>
                </Field>
              </div>
            </Section>

            {/* Upsell-family types inherit the parent contract's renewal/term — hide the section. */}
            {!isUpsellByName && (
              <Section title="Renewal & Term">
                <div className="grid grid-cols-3 gap-4">
                  <Field label="Auto-renews (evergreen)">
                    <div className="flex h-9 items-center">
                      <Switch checked={autoRenew} onCheckedChange={setAutoRenew} />
                    </div>
                  </Field>
                  {autoRenew && (
                    <Field label="Renews Into">
                      <Select value={renewalTerm} onValueChange={(v) => setRenewalTerm(v as RenewalTerm)}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {RENEWAL_TERMS.map((t) => (
                            <SelectItem key={t.value} value={t.value}>
                              {t.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </Field>
                  )}
                  {autoRenew && renewalTerm === "DIFFERENT_TERM" && (
                    <Field label="Renewal Term (months)">
                      <NumberInput value={renewalTermMonths} onChange={setRenewalTermMonths} maxDecimals={0} />
                    </Field>
                  )}
                  {autoRenew && renewalTerm === "MONTH_TO_MONTH" && (
                    <Field label="Month-to-Month Uplift (%)">
                      <NumberInput value={mtmUplift} onChange={setMtmUplift} />
                    </Field>
                  )}
                  {autoRenew && (
                    <Field label="Notice Period (days)">
                      <NumberInput value={noticeDays} onChange={setNoticeDays} maxDecimals={0} />
                    </Field>
                  )}
                </div>
                {!autoRenew && (
                  <p className="text-xs text-muted-foreground">
                    This contract does not auto-renew — it expires at the end of its {termMonths || ""}-month term.
                  </p>
                )}
                {contractId && detail?.next_renewal_when && (
                  <p className="text-xs text-muted-foreground">Next renewal date: {dateOnly(detail.next_renewal_when)}</p>
                )}
              </Section>
            )}

            <Section title="Sales">
              <div className="grid grid-cols-2 gap-4">
                <Field label="Closer">
                  <Combobox options={closerOptions} value={closerId} onChange={setCloserId} placeholder="Select a closer…" clearable />
                </Field>
                <Field label="Closer HubSpot Owner ID">
                  <Input value={closerHubspot} onChange={(e) => setCloserHubspot(e.target.value)} />
                </Field>
              </div>
              <div className="flex items-end gap-3">
                <Field label="PandaDoc ID" className="flex-1">
                  <Input value={pandadocId} onChange={(e) => setPandadocId(e.target.value)} readOnly={!editThirdParty} className={cn(!editThirdParty && "bg-muted")} />
                </Field>
                <Field label="Stripe Customer ID" className="flex-1">
                  <Input value={stripeId} onChange={(e) => setStripeId(e.target.value)} readOnly={!editThirdParty} className={cn(!editThirdParty && "bg-muted")} />
                </Field>
                <Field label="HubSpot Deal ID" className="flex-1">
                  <Input value={hubspotDealId} onChange={(e) => setHubspotDealId(e.target.value)} readOnly={!editThirdParty} className={cn(!editThirdParty && "bg-muted")} />
                </Field>
                <Button variant="outline" size="icon" aria-label="Toggle 3rd-party fields" onClick={() => setEditThirdParty((v) => !v)}>
                  {editThirdParty ? <Unlock className="h-3.5 w-3.5" /> : <Lock className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </Section>

            <Section title={`Line Items — MRR ${(mrrTotal ?? 0).toLocaleString(undefined, { style: "currency", currency: "USD" })}${lineRevenue ? ` · lines ${lineRevenue.toLocaleString(undefined, { style: "currency", currency: "USD" })}` : ""}`}>
              <div className="flex flex-col gap-2">
                {lines.map((l) => {
                  const m = lineMarginPct(l);
                  return (
                    <div key={l._id} className="grid grid-cols-[1fr_4.5rem_6rem_6rem_5rem_auto] items-center gap-2">
                      <Combobox options={productOptions} value={l.product} onChange={(v) => patchLine(l._id, { product: v })} placeholder="Item or package…" />
                      <NumberInput value={l.qty} onChange={(v) => patchLine(l._id, { qty: v })} placeholder="Qty" />
                      <NumberInput value={l.price_per_unit} onChange={(v) => patchLine(l._id, { price_per_unit: v })} prefix="$" placeholder="Price" />
                      <NumberInput value={l.overage_price_per_unit} onChange={(v) => patchLine(l._id, { overage_price_per_unit: v })} prefix="$" placeholder="Overage" />
                      <span className={cn("text-right text-xs tabular-nums", marginTone(m))}>
                        {m == null ? "—" : `${m.toFixed(1)}%`}
                        {belowFloor(l) && <span className="block text-danger">⚠ floor</span>}
                      </span>
                      <Button variant="ghost" size="icon" aria-label="Remove line" onClick={() => removeLine(l._id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  );
                })}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setLines((ls) => [...ls, { _id: uid("line"), product: null, qty: 1, price_per_unit: null, overage_price_per_unit: null }])}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add line
                  </Button>
                </div>
              </div>
            </Section>

            <Section title="Discounts">
              <div className="flex flex-col gap-2">
                {discs.map((d) => (
                  <div key={d._id} className="grid grid-cols-[1fr_1fr_6rem_auto] items-center gap-2">
                    <Combobox options={discountOptions} value={d.discount} onChange={(v) => patchDisc(d._id, { discount: v })} placeholder="Discount…" />
                    <Select value={d.target} onValueChange={(v) => patchDisc(d._id, { target: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={WHOLE_CONTRACT}>Whole contract</SelectItem>
                        {lines
                          .filter((l) => l.product)
                          .map((l, i) => (
                            <SelectItem key={l._id} value={l._id}>
                              {`Line ${i + 1}: ${productOptions.find((o) => o.id === l.product)?.label ?? ""}`}
                            </SelectItem>
                          ))}
                      </SelectContent>
                    </Select>
                    <NumberInput value={d.duration_override} onChange={(v) => patchDisc(d._id, { duration_override: v })} maxDecimals={0} placeholder="Duration" />
                    <Button variant="ghost" size="icon" aria-label="Remove discount" onClick={() => setDiscs((ds) => ds.filter((x) => x._id !== d._id))}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setDiscs((ds) => [...ds, { _id: uid("disc"), discount: null, target: WHOLE_CONTRACT, duration_override: null }])}
                  >
                    <Plus className="h-3.5 w-3.5" />
                    Add discount
                  </Button>
                </div>
              </div>
            </Section>
          </div>
        )}

        {save.error && (
          <Alert variant="danger">
            <AlertDescription>{writeError(save.error)}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={save.isPending || missingReqs}>
            {save.isPending ? "Saving…" : contractId ? "Save changes" : "Create contract"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
